import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useRedeemsModal } from '../hooks/redeems-modal.hook';

type RedeemsModalProps = {
  productHook: any;
};

const TABLE_CONTAINER_HEIGHT = 350;
const TABLE_STATE_ROW_HEIGHT = 440;

export default function RedeemsModal({ productHook }: RedeemsModalProps) {
  const hook = useRedeemsModal({ productHook });
  const t = useTheme();

  const renderRows = () => {
    if (hook.loadingFilteredRedeems) {
      return (
        <TableRow>
          <TableCell colSpan={2} align="center" sx={{ py: 0, height: TABLE_STATE_ROW_HEIGHT }}>
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!hook.partner) {
      return (
        <TableRow>
          <TableCell colSpan={2} align="center" sx={{ py: 0, height: TABLE_STATE_ROW_HEIGHT }}>
            Selecione um parceiro para buscar os resgatáveis.
          </TableCell>
        </TableRow>
      );
    }

    if (!hook.filteredRedeems?.length) {
      return (
        <TableRow>
          <TableCell colSpan={2} align="center" sx={{ py: 0, height: TABLE_STATE_ROW_HEIGHT }}>
            Nenhum resgatável encontrado.
          </TableCell>
        </TableRow>
      );
    }

    return hook.filteredRedeems.map((redeem: any) => (
      <TableRow
        hover
        key={redeem._id}
        selected={hook.selectedRedeemId === redeem._id}
        onClick={() => hook.onSelectRedeem(redeem)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell padding="checkbox">
          <Radio checked={hook.selectedRedeemId === redeem._id} />
        </TableCell>
        <TableCell align="left" sx={{ pl: 2 }}>
          {redeem.name}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Drawer anchor="right" open={productHook.openRedeemModal} onClose={hook.onClose}>
      <Stack
        spacing={3}
        sx={{
          p: 3,
          width: {
            xs: '100vw',
            sm: 560,
          },
          maxWidth: '100vw',
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontWeight="bold">Associar Resgatável</Typography>
          <IconButton onClick={hook.onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <FormControl fullWidth size="small">
          <InputLabel id="partner-redeem-select">Parceiro</InputLabel>
          <Select
            labelId="partner-redeem-select"
            variant="filled"
            label="Parceiro"
            value={hook.partner}
            onChange={(e) => hook.onSelectPartner(e.target.value)}
          >
            {(productHook.part || []).map((option: any) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          disabled={!hook.partner}
          // size="small"
          variant="filled"
          value={hook.name}
          label="Resgatável"
          placeholder="Digite o nome do resgatável"
          onChange={(e) => hook.onChangeName(e.target.value)}
        />
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <TableContainer
            sx={{
              height: TABLE_CONTAINER_HEIGHT,
              overflowY: 'auto',
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: `${t.palette.grey[400]} !important`,
                  backgroundImage: 'none !important',
                  color: t.palette.grey[900],
                },
              }}
            >
              <TableHead
                sx={{
                  bgcolor: t.palette.grey[400],
                }}
              >
                <TableRow>
                  <TableCell width={48} sx={{ bgcolor: t.palette.grey[400] }} />
                  <TableCell
                    align="left"
                    sx={{ bgcolor: t.palette.grey[400], color: t.palette.grey[900], pl: 2 }}
                  >
                    Nome
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            page={hook.page}
            count={hook.filteredRedeemsTotal}
            rowsPerPage={hook.limit}
            rowsPerPageOptions={[hook.limit]}
            onRowsPerPageChange={() => null}
            onPageChange={(_, newPage) => hook.onChangePage(newPage)}
            labelRowsPerPage="Resultados por Página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `Mais de ${to}`}`
            }
            disabled={hook.loadingFilteredRedeems}
          />
        </Box>
        {!!hook.selectedRedeemName && (
          <Stack
            direction="row"
            sx={{ p: 2, backgroundColor: (theme) => theme.palette.primary.main, borderRadius: 1 }}
            spacing={1}
            justifyContent="start"
          >
            <InfoIcon sx={{ color: 'white' }} />
            <Typography sx={{ color: 'white' }} variant="body2">
              Resgatável selecionado: <strong>{hook.selectedRedeemName}</strong>
            </Typography>
          </Stack>
        )}
        <Stack
          spacing={2}
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="outlined" onClick={hook.onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
            }}
            disabled={!hook.partner || !hook.selectedRedeemId}
            onClick={hook.onConfirm}
          >
            Associar
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
